import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 语言学习 - 竖屏「单词卡」风格
 *
 * 核心亮点：
 * - 单词卡翻转动画（3D perspective）
 * - 发音音标 + 音节拆分高亮
 * - 词根词缀拆解可视化
 * - 例句关键词高亮
 * - 记忆技巧卡片
 *
 * 尺寸：1080x1920 竖屏
 */

interface WordPart {
  text: string;
  type: 'prefix' | 'root' | 'suffix';
  meaning: string;
  color: string;
}

interface ExampleSentence {
  en: string;
  zh: string;
  highlightWord: string;
}

interface LanguageCardTemplateProps {
  word: string;
  phonetic: string;
  partOfSpeech: string;
  meaning: string;
  wordParts: WordPart[];
  examples: ExampleSentence[];
  synonyms: string[];
  memoryTip: string;
  level: string;
}

const DEFAULT_PROPS: LanguageCardTemplateProps = {
  word: 'unprecedented',
  phonetic: '/ʌnˈpresɪdentɪd/',
  partOfSpeech: 'adj.',
  meaning: '前所未有的，史无前例的',
  wordParts: [
    { text: 'un-', type: 'prefix', meaning: '否定', color: '#ef4444' },
    { text: 'pre-', type: 'prefix', meaning: '在…之前', color: '#f97316' },
    { text: 'ced', type: 'root', meaning: '走，行', color: '#818cf8' },
    { text: '-ent', type: 'suffix', meaning: '形容词后缀', color: '#22c55e' },
    { text: '-ed', type: 'suffix', meaning: '过去分词', color: '#14b8a6' },
  ],
  examples: [
    { en: 'This is an unprecedented crisis in modern history.', zh: '这是现代史上前所未有的危机。', highlightWord: 'unprecedented' },
    { en: 'The company achieved unprecedented growth this year.', zh: '公司今年取得了史无前例的增长。', highlightWord: 'unprecedented' },
  ],
  synonyms: ['unparalleled', 'unheard-of', 'extraordinary'],
  memoryTip: 'un(不) + pre(前) + ced(走) = 以前没走过的路 → 前所未有',
  level: 'CET-6 / IELTS 7+',
};

export const LanguageCardTemplate: React.FC<Partial<LanguageCardTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Card flip: 0-60 frames
  const flipProgress = interpolate(frame, [15, 55], [0, 180], { extrapolateRight: 'clamp' });
  const showBack = flipProgress > 90;
  const cardRotateY = flipProgress <= 90 ? flipProgress : 180 - flipProgress;
  const cardOpacity = spring({ frame: frame - 5, fps, from: 0, to: 1, config: { damping: 18, stiffness: 80 } });

  // Word parts
  const partsStart = 70;
  const partsInterval = 25;

  // Examples
  const exStart = partsStart + p.wordParts.length * partsInterval + 30;
  const exInterval = 50;

  // Synonyms
  const synStart = exStart + p.examples.length * exInterval + 20;

  // Memory tip
  const tipStart = synStart + 40;
  const tipOpacity = interpolate(frame, [tipStart, tipStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Phonetic wave animation
  const waveVisible = frame > 40 && frame < 120;

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #fefce8 0%, #fff7ed 50%, #fef2f2 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: '#1e293b',
        overflow: 'hidden',
      }}
    >
      {/* Level badge */}
      <div
        style={{
          position: 'absolute',
          top: 30,
          right: 60,
          padding: '6px 16px',
          background: '#818cf812',
          border: '1px solid #818cf830',
          borderRadius: 8,
          fontSize: 30,
          color: '#818cf8',
          fontWeight: 600,
          opacity: cardOpacity,
        }}
      >
        {p.level}
      </div>

      {/* ========== WORD CARD ========== */}
      <div
        style={{
          position: 'absolute',
          top: 100,
          left: 60,
          right: 60,
          perspective: '1000px',
          opacity: cardOpacity,
        }}
      >
        <div
          style={{
            padding: '40px',
            background: 'white',
            borderRadius: 28,
            boxShadow: '0 8px 40px rgba(0,0,0,0.06)',
            border: '1px solid rgba(0,0,0,0.04)',
            transform: `rotateY(${showBack ? 0 : cardRotateY}deg)`,
            textAlign: 'center',
          }}
        >
          {!showBack ? (
            <>
              <div style={{ fontSize: 22, color: '#818cf8', fontWeight: 600, marginBottom: 12 }}>VOCABULARY</div>
              <div style={{ fontSize: 64, fontWeight: 900, letterSpacing: '-0.02em', marginBottom: 8 }}>{p.word}</div>
              <div style={{ fontSize: 24, color: '#64748b', fontFamily: '"Times New Roman", serif', fontStyle: 'italic' }}>{p.phonetic}</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 64, fontWeight: 900, marginBottom: 8 }}>{p.word}</div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 42, color: '#818cf8', fontWeight: 600 }}>{p.partOfSpeech}</span>
                <span style={{ fontSize: 42, color: '#64748b' }}>{p.phonetic}</span>
              </div>
              <div style={{ fontSize: 52, fontWeight: 700, color: '#1e293b' }}>{p.meaning}</div>
            </>
          )}
        </div>
      </div>

      {/* Phonetic wave */}
      {waveVisible && (
        <div style={{ position: 'absolute', top: 380, left: 0, right: 0, display: 'flex', justifyContent: 'center' }}>
          <svg width="300" height="40" viewBox="0 0 300 40">
            {Array.from({ length: 30 }).map((_, i) => {
              const h = 4 + Math.abs(Math.sin(frame * 0.15 + i * 0.5)) * 28;
              return (
                <rect
                  key={i}
                  x={i * 10}
                  y={20 - h / 2}
                  width={4}
                  height={h}
                  rx={2}
                  fill="#818cf8"
                  opacity={interpolate(frame, [40, 60, 100, 120], [0, 0.6, 0.6, 0], { extrapolateRight: 'clamp' })}
                />
              );
            })}
          </svg>
        </div>
      )}

      {/* ========== WORD PARTS (词根词缀拆解) ========== */}
      <div
        style={{
          position: 'absolute',
          top: 470,
          left: 60,
          right: 60,
        }}
      >
        <div style={{ fontSize: 36, color: '#94a3b8', letterSpacing: '0.1em', marginBottom: 30 }}>词根词缀拆解</div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
          {p.wordParts.map((part, i) => {
            const start = partsStart + i * partsInterval;
            const scale = spring({
              frame: frame - start,
              fps,
              from: 0,
              to: 1,
              config: { damping: 10, stiffness: 150, mass: 0.5 },
            });

            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                  opacity: scale,
                  transform: `scale(${0.8 + scale * 0.2})`,
                }}
              >
                <div
                  style={{
                    padding: '10px 18px',
                    background: `${part.color}12`,
                    border: `2px solid ${part.color}40`,
                    borderRadius: 14,
                    fontSize: 32,
                    fontWeight: 800,
                    color: part.color,
                  }}
                >
                  {part.text}
                </div>
                <div style={{ fontSize: 32, color: '#64748b' }}>{part.meaning}</div>
                <div
                  style={{
                    fontSize: 28,
                    padding: '2px 8px',
                    background: `${part.color}10`,
                    borderRadius: 4,
                    color: part.color,
                    fontWeight: 600,
                  }}
                >
                  {part.type}
                </div>
              </div>
            );
          })}
        </div>
        {/* Connection arrows */}
        <div style={{ textAlign: 'center', marginTop: 10, fontSize: 24, color: '#cbd5e1' }}>
          {'→ → → →'.split(' ').map((a, i) => (
            <span key={i} style={{ margin: '0 16px', opacity: interpolate(frame, [partsStart + i * partsInterval, partsStart + (i + 1) * partsInterval], [0, 0.5], { extrapolateRight: 'clamp' }) }}>{a}</span>
          ))}
        </div>
      </div>

      {/* ========== EXAMPLES ========== */}
      <div
        style={{
          position: 'absolute',
          top: 780,
          left: 60,
          right: 60,
        }}
      >
        <div style={{ fontSize: 36, color: '#94a3b8', letterSpacing: '0.1em', marginBottom: 14 }}>例句</div>
        {p.examples.map((ex, i) => {
          const start = exStart + i * exInterval;
          const opacity = interpolate(frame, [start, start + 20], [0, 1], { extrapolateRight: 'clamp' });
          const x = interpolate(frame, [start, start + 20], [20, 0], { extrapolateRight: 'clamp' });

          const parts = ex.en.split(new RegExp(`(${ex.highlightWord})`, 'i'));

          return (
            <div
              key={i}
              style={{
                padding: '18px 22px',
                background: 'white',
                borderRadius: 16,
                border: '1px solid rgba(0,0,0,0.04)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
                marginBottom: 14,
                opacity,
                transform: `translateX(${x}px)`,
              }}
            >
              <div style={{ fontSize: 36, lineHeight: 1.5, marginBottom: 6 }}>
                {parts.map((part, pi) =>
                  part.toLowerCase() === ex.highlightWord.toLowerCase() ? (
                    <span key={pi} style={{ color: '#818cf8', fontWeight: 700, borderBottom: '2px solid #818cf8' }}>{part}</span>
                  ) : (
                    <span key={pi}>{part}</span>
                  )
                )}
              </div>
              <div style={{ fontSize: 32, color: '#64748b' }}>{ex.zh}</div>
            </div>
          );
        })}
      </div>

      {/* ========== SYNONYMS ========== */}
      <div
        style={{
          position: 'absolute',
          top: 1280,
          left: 60,
          right: 60,
          opacity: interpolate(frame, [synStart, synStart + 20], [0, 1], { extrapolateRight: 'clamp' }),
        }}
      >
        <div style={{ fontSize: 36, color: '#94a3b8', letterSpacing: '0.1em', marginBottom: 10 }}>近义词</div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {p.synonyms.map((s, i) => {
            const opacity = interpolate(frame, [synStart + i * 12, synStart + i * 12 + 15], [0, 1], { extrapolateRight: 'clamp' });
            return (
              <div
                key={i}
                style={{
                  padding: '8px 20px',
                  background: '#f1f5f9',
                  borderRadius: 20,
                  fontSize: 34,
                  fontWeight: 600,
                  color: '#475569',
                  opacity,
                }}
              >
                {s}
              </div>
            );
          })}
        </div>
      </div>

      {/* ========== MEMORY TIP ========== */}
      <div
        style={{
          position: 'absolute',
          bottom: 120,
          left: 60,
          right: 60,
          opacity: tipOpacity,
        }}
      >
        <div
          style={{
            padding: '22px 28px',
            background: 'linear-gradient(135deg, #818cf808, #f472b608)',
            border: '1px solid #818cf820',
            borderRadius: 20,
          }}
        >
          <div style={{ fontSize: 38, color: '#818cf8', fontWeight: 600, marginBottom: 8, letterSpacing: '0.08em' }}>💡 记忆技巧</div>
          <div style={{ fontSize: 38, fontWeight: 700, color: '#1e293b', lineHeight: 1.5 }}>{p.memoryTip}</div>
        </div>
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 480], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 4,
          background: 'linear-gradient(90deg, #818cf8, #f472b6)',
        }}
      />
    </AbsoluteFill>
  );
};
